import { useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { calculateChessProbabilities, DEFAULT_CHESS_MODEL_PARAMETERS } from "../helpers/chessProbability";

const emptyPlayer = (id, drawFactor) => ({ id, name: "", elo: "", drawFactor });
const PARAMETER_FIELDS = [
  "baseDrawProbability", "referenceElo", "eloStep", "eloRelativeEffect",
  "referenceMinutes", "timeRelativeEffect", "defaultPlayerDrawFactor", "whiteAdvantage",
];
const ELO_LEVELS = [1800, 2000, 2200, 2400, 2600, 2800];
const percent = (value) => `${(value * 100).toFixed(1)} %`;

export function ChessWorkspace() {
  const { t } = useTranslation();
  const nextPlayerId = useRef(2);
  const [parameters, setParameters] = useState({ ...DEFAULT_CHESS_MODEL_PARAMETERS });
  const [effectiveMinutes, setEffectiveMinutes] = useState(15);
  const [referenceElo, setReferenceElo] = useState(2200);
  const [players, setPlayers] = useState([
    emptyPlayer(1, DEFAULT_CHESS_MODEL_PARAMETERS.defaultPlayerDrawFactor),
  ]);

  const updatePlayer = (id, field, value) => setPlayers((current) => current.map((player) => (
    player.id === id ? { ...player, [field]: value } : player
  )));
  const addPlayer = () => {
    const id = nextPlayerId.current++;
    setPlayers((current) => [...current, emptyPlayer(id, parameters.defaultPlayerDrawFactor)]);
  };
  const removePlayer = (id) => setPlayers((current) => current.filter((player) => player.id !== id));
  const updateParameter = (field, value) => setParameters((current) => ({ ...current, [field]: value }));
  const resetParameters = () => setParameters({ ...DEFAULT_CHESS_MODEL_PARAMETERS });

  const calculateEqualGame = (elo) => calculateChessProbabilities({
    player1Elo: elo,
    player2Elo: elo,
    player1Color: "white",
    effectiveMinutes,
    player1DrawFactor: parameters.defaultPlayerDrawFactor,
    player2DrawFactor: parameters.defaultPlayerDrawFactor,
    parameters,
  });
  const preview = useMemo(
    () => calculateEqualGame(referenceElo),
    // calculateEqualGame deliberately represents the current form state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveMinutes, parameters, referenceElo],
  );
  const eloComparison = useMemo(
    () => ELO_LEVELS.map((elo) => ({ elo, draw: calculateEqualGame(elo).draw })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectiveMinutes, parameters],
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pb: 5 }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: "primary.main", mb: 3 }}>
        {t("help.controlCenter.chess.title")}
      </Typography>
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              {t("help.controlCenter.chess.players")}
            </Typography>
            {players.map((player) => (
              <Stack key={player.id} direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                <TextField label={t("help.controlCenter.chess.name")} value={player.name} onChange={(event) => updatePlayer(player.id, "name", event.target.value)} fullWidth size="small" />
                <TextField label={t("help.controlCenter.chess.elo")} value={player.elo} onChange={(event) => updatePlayer(player.id, "elo", event.target.value)} type="number" inputProps={{ min: 0 }} size="small" sx={{ width: { xs: "100%", md: 180 } }} />
                <TextField label={t("help.controlCenter.chess.playerDrawFactor")} value={player.drawFactor} onChange={(event) => updatePlayer(player.id, "drawFactor", event.target.value)} type="number" inputProps={{ step: 0.01 }} size="small" sx={{ width: { xs: "100%", md: 230 } }} />
                <IconButton aria-label={t("help.controlCenter.chess.removePlayer")} onClick={() => removePlayer(player.id)} color="error"><DeleteOutline /></IconButton>
              </Stack>
            ))}
            <Box><Button startIcon={<Add />} onClick={addPlayer} variant="outlined">{t("help.controlCenter.chess.addPlayer")}</Button></Box>
          </Stack>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(320px, 0.65fr)" }, gap: 3 }}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{t("help.controlCenter.chess.modelParameters")}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                {PARAMETER_FIELDS.map((field) => (
                  <TextField key={field} label={t(`help.controlCenter.chess.parameters.${field}`)} value={parameters[field]} onChange={(event) => updateParameter(field, event.target.value)} type="number" inputProps={{ step: "any" }} size="small" />
                ))}
              </Box>
              <Box><Button onClick={resetParameters} variant="outlined">{t("help.controlCenter.chess.restoreDefaults")}</Button></Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: "rgba(123,60,89,0.04)" }}>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{t("help.controlCenter.chess.modelPreview")}</Typography>
              <TextField label={t("help.controlCenter.chess.referenceElo")} value={referenceElo} onChange={(event) => setReferenceElo(event.target.value)} type="number" inputProps={{ min: 0 }} size="small" />
              <TextField label={t("help.controlCenter.chess.effectiveMinutes")} value={effectiveMinutes} onChange={(event) => setEffectiveMinutes(event.target.value)} type="number" inputProps={{ min: 0 }} size="small" />
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                {[5, 20, 90].map((minutes) => <Button key={minutes} size="small" onClick={() => setEffectiveMinutes(minutes)}>{t(`help.controlCenter.chess.timePresets.${minutes}`)}</Button>)}
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}>
                {[["whiteWins", preview.player1Win], ["draw", preview.draw], ["blackWins", preview.player2Win]].map(([label, value]) => (
                  <Box key={label} sx={{ textAlign: "center", p: 1.25, bgcolor: "background.paper", borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t(`help.controlCenter.chess.${label}`)}</Typography>
                    <Typography data-testid={`preview-${label}`} sx={{ fontWeight: 800 }}>{percent(value)}</Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>{t("help.controlCenter.chess.eloComparison")}</Typography>
          <Stack spacing={0.75}>
            {eloComparison.map(({ elo, draw }) => (
              <Stack key={elo} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography>{elo}</Typography><Typography data-testid={`elo-draw-${elo}`} sx={{ fontWeight: 700 }}>{percent(draw)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
